import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db, auth } from "./firebase";
import { logAuditEvent, AuditEvents } from "./auditService";
import { orgCollection, orgDoc } from "./orgPaths";

// ===================== INDIVIDUALS =====================

/**
 * Get all individuals assigned to the current agent
 */
export async function getMyIndividuals() {
  const userId = auth.currentUser?.uid;
  if (!userId) throw new Error("Not authenticated");

  const q = query(
    orgCollection("individuals"),
    where("assignedAgents", "array-contains", userId),
    orderBy("lastName", "asc")
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
}

/**
 * Get a single individual by ID
 */
export async function getIndividual(individualId) {
  const docRef = orgDoc("individuals", individualId);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    logAuditEvent(AuditEvents.INDIVIDUAL_VIEWED, { individualId });
    return { id: docSnap.id, ...docSnap.data() };
  }
  return null;
}

/**
 * Get individuals with visits due today
 */
export async function getTodayVisits() {
  const userId = auth.currentUser?.uid;
  if (!userId) throw new Error("Not authenticated");

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const q = query(
    orgCollection("individuals"),
    where("assignedAgents", "array-contains", userId),
    where("nextVisit", ">=", Timestamp.fromDate(today)),
    where("nextVisit", "<", Timestamp.fromDate(tomorrow))
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
}

/**
 * Get overdue visits
 */
export async function getOverdueVisits() {
  const userId = auth.currentUser?.uid;
  if (!userId) throw new Error("Not authenticated");

  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const q = query(
    orgCollection("individuals"),
    where("assignedAgents", "array-contains", userId),
    where("nextVisit", "<", Timestamp.fromDate(now)),
    orderBy("nextVisit", "asc")
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
}

// ===================== SESSIONS =====================

/**
 * Create a new session record
 */
export async function createSession(sessionData) {
  const userId = auth.currentUser?.uid;
  if (!userId) throw new Error("Not authenticated");

  const session = {
    agentId: userId,
    individualId: sessionData.individualId,
    date: serverTimestamp(),
    locationType: sessionData.locationType,
    location: sessionData.location,
    serviceType: sessionData.serviceType,
    status: "active", // active, processing, completed
    duration: null,
    audioUploaded: false,
    noteGenerated: false,
    createdAt: serverTimestamp(),
  };

  const docRef = await addDoc(orgCollection("sessions"), session);

  logAuditEvent(AuditEvents.SESSION_START, {
    sessionId: docRef.id,
    individualId: sessionData.individualId,
  });

  return { id: docRef.id, ...session };
}

/**
 * Update session status
 */
export async function updateSession(sessionId, updates) {
  const sessionRef = orgDoc("sessions", sessionId);
  await updateDoc(sessionRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Get sessions for current agent
 */
export async function getMySessions(limitCount = 20) {
  const userId = auth.currentUser?.uid;
  if (!userId) throw new Error("Not authenticated");

  const q = query(
    orgCollection("sessions"),
    where("agentId", "==", userId),
    orderBy("date", "desc"),
    limit(limitCount)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
}

// ===================== NOTES =====================

/**
 * Save a generated note (draft or submitted)
 */
export async function saveNote(noteData) {
  const userId = auth.currentUser?.uid;
  if (!userId) throw new Error("Not authenticated");

  const note = {
    agentId: userId,
    sessionId: noteData.sessionId,
    individualId: noteData.individualId,
    status: noteData.status || "draft", // draft, submitted, revised
    content: noteData.content,
    billingInfo: noteData.billingInfo,
    confidence: noteData.confidence,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    submittedAt: noteData.status === "submitted" ? serverTimestamp() : null,
  };

  const docRef = await addDoc(orgCollection("notes"), note);

  const eventType =
    noteData.status === "submitted"
      ? AuditEvents.NOTE_SUBMITTED
      : AuditEvents.NOTE_DRAFT_SAVED;

  logAuditEvent(eventType, {
    noteId: docRef.id,
    sessionId: noteData.sessionId,
    individualId: noteData.individualId,
  });

  return { id: docRef.id, ...note };
}

/**
 * Update an existing note
 */
export async function updateNote(noteId, updates) {
  const noteRef = orgDoc("notes", noteId);
  await updateDoc(noteRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });

  logAuditEvent(AuditEvents.NOTE_EDITED, {
    noteId,
    fieldsEdited: Object.keys(updates),
  });
}

/**
 * Submit a draft note
 */
export async function submitNote(noteId) {
  const noteRef = orgDoc("notes", noteId);
  await updateDoc(noteRef, {
    status: "submitted",
    submittedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  logAuditEvent(AuditEvents.NOTE_SUBMITTED, { noteId });
}

/**
 * Get notes for a specific individual
 */
export async function getNotesForIndividual(individualId, limitCount = 10) {
  const q = query(
    orgCollection("notes"),
    where("individualId", "==", individualId),
    orderBy("createdAt", "desc"),
    limit(limitCount)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
}

/**
 * Get all notes for current agent
 */
export async function getMyNotes(status = null, limitCount = 20) {
  const userId = auth.currentUser?.uid;
  if (!userId) throw new Error("Not authenticated");

  let q;
  if (status) {
    q = query(
      orgCollection("notes"),
      where("agentId", "==", userId),
      where("status", "==", status),
      orderBy("createdAt", "desc"),
      limit(limitCount)
    );
  } else {
    q = query(
      orgCollection("notes"),
      where("agentId", "==", userId),
      orderBy("createdAt", "desc"),
      limit(limitCount)
    );
  }

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
}

/**
 * Get pending draft notes count
 */
export async function getPendingDraftCount() {
  const userId = auth.currentUser?.uid;
  if (!userId) return 0;

  const q = query(
    orgCollection("notes"),
    where("agentId", "==", userId),
    where("status", "==", "draft")
  );

  const snapshot = await getDocs(q);
  return snapshot.size;
}

// ===================== SYNC QUEUE =====================

/**
 * Add item to sync queue for offline processing
 */
export async function addToSyncQueue(item) {
  const userId = auth.currentUser?.uid;
  if (!userId) throw new Error("Not authenticated");

  await addDoc(orgCollection("syncQueue"), {
    agentId: userId,
    type: item.type, // "audio_upload", "note_generation", "note_submit"
    data: item.data,
    status: "pending",
    createdAt: serverTimestamp(),
    retries: 0,
  });
}

/**
 * Get pending sync items
 */
export async function getPendingSyncItems() {
  const userId = auth.currentUser?.uid;
  if (!userId) return [];

  const q = query(
    orgCollection("syncQueue"),
    where("agentId", "==", userId),
    where("status", "==", "pending"),
    orderBy("createdAt", "asc")
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
}
